import React from 'react';
import Modal from './modal';
import Constants from "../constants";
import { inclusiveIntList } from "../utils";
import { DifficultyLevel, GameOptions } from "../types";
import { PersistantBoolean, PersistantNumber, PersistantString } from "../persistantValue";

interface OptionsPopupState {
    height: number;
    width: number;
    size: number;
    squareBoard: boolean;
    eliminateRedsFirst: boolean;
    sameSolutionAsPreviousGame: boolean;
    difficulty: DifficultyLevel;
    includeSelfInSeenBluesCount: boolean;
    isOpen: boolean;
}

class OptionsPopup extends React.Component<{}, OptionsPopupState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            height: Constants.heightPV.getValue(),
            width: Constants.widthPV.getValue(),
            size: Constants.sizePV.getValue(),
            squareBoard: Constants.squareBoardPV.getValue(),
            eliminateRedsFirst: Constants.defaultGameOptions.eliminateRedsFirst,
            sameSolutionAsPreviousGame: Constants.defaultGameOptions.sameSolutionAsPreviousGame,
            difficulty: Constants.difficultyPV.getValue() as DifficultyLevel,
            includeSelfInSeenBluesCount: Constants.includeSelfInSeenBluesCountPV.getValue(),
            isOpen: false,
        };
    }

    setSelectOptionFn(pv: PersistantNumber | PersistantString | PersistantBoolean) {
        return (event: React.ChangeEvent<HTMLSelectElement>) => {
            const optionString = event.target.value;
            pv.setValue(optionString);
            this.setState({ [pv.name]: optionString } as any);
        };
    }

    setDifficulty = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value as DifficultyLevel;
        Constants.difficultyPV.setValue(value);
        this.setState({ difficulty: value });
    };

    setSquare = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        Constants.squareBoardPV.setValue(checked);
        this.setState({ squareBoard: checked });
    };

    setBoardShape = (square: boolean) => {
        Constants.squareBoardPV.setValue(square);
        this.setState({ squareBoard: square });
    };

    setEliminateRedsFirst = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        this.setState({ eliminateRedsFirst: checked });
    };

    setSameSolutionAsPreviousGame = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        this.setState({ sameSolutionAsPreviousGame: checked });
    };

    setIncludeSelfInSeenBluesCount = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        Constants.includeSelfInSeenBluesCountPV.setValue(checked);
        this.setState({ includeSelfInSeenBluesCount: checked });
    };

    getGameOptions(): GameOptions {
        const size =
            Constants.squareBoardPV.getValue() ? {
                height: Constants.sizePV.getValue(),
                width: Constants.sizePV.getValue(),
            } :
                {
                    height: Constants.heightPV.getValue(),
                    width: Constants.widthPV.getValue(),
                };
        console.log(size);
        const { eliminateRedsFirst, sameSolutionAsPreviousGame, difficulty } = this.state;
        const { includeSelfInSeenBluesCount } = this.state;
        const displayOptions = {
            includeSelfInSeenBluesCount
        };

        return {
            ...Constants.defaultGameOptions,
            size,
            eliminateRedsFirst,
            sameSolutionAsPreviousGame,
            difficulty,
            displayOptions,
        };
    }

    render() {
        return (<>
            <button className={'button game-button__options'} onClick={() => this.setState({ isOpen: true })}>Options</button>

            <Modal isOpen={this.state.isOpen} onClose={() => this.setState({ isOpen: false })}
                title="Kuromasu Options"
                dialogStyles={{ maxHeight: '520px' }}>

                <div className="modal__section">
                    <label className="modal__row">
                        <span>Difficulty:</span>
                        <select name="difficulty" value={this.state.difficulty} onChange={this.setDifficulty}>
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                        </select>
                    </label>
                    <p className="modal__hint">
                        In hard difficulty, the hints provided are minimal and every one is needed to find the solution. In the others, some superfluous information is provided, making it a little easier to solve.
                    </p>
                </div>

                <div className="modal__section">
                    <label className="modal__row">
                        <span>Board:</span>
                        <span className="modal__toggle-group">
                            <button
                                className={`modal__toggle ${this.state.squareBoard ? 'modal__toggle--active' : ''}`}
                                onClick={() => this.setBoardShape(true)}>
                                Square
                            </button>
                            <button
                                className={`modal__toggle ${!this.state.squareBoard ? 'modal__toggle--active' : ''}`}
                                onClick={() => this.setBoardShape(false)}>
                                Rectangle
                            </button>
                        </span>
                    </label>
                    <label className="modal__row">
                        {this.state.squareBoard ? (
                            <>
                                <span>Size:</span>
                                <select name="size" value={this.state.size} onChange={this.setSelectOptionFn(Constants.sizePV)}>
                                    {inclusiveIntList(Constants.minSelectableGridSize, Constants.maxSelectableGridSize).map(n =>
                                        <option key={n} value={n}>{n}</option>
                                    )}
                                </select>
                            </>
                        ) : (
                            <>
                                <span>Height:</span>
                                <select name="height" value={this.state.height} onChange={this.setSelectOptionFn(Constants.heightPV)}>
                                    {inclusiveIntList(Constants.minSelectableGridSize, Constants.maxSelectableGridSize).map(n =>
                                        <option key={n} value={n}>{n}</option>
                                    )}
                                </select>
                                <span style={{ marginLeft: '12px' }}>Width:</span>
                                <select name="width" value={this.state.width} onChange={this.setSelectOptionFn(Constants.widthPV)}>
                                    {inclusiveIntList(Constants.minSelectableGridSize, Constants.maxSelectableGridSize).map(n =>
                                        <option key={n} value={n}>{n}</option>
                                    )}
                                </select>
                            </>
                        )}
                    </label>
                </div>

                <div className="modal__section modal__section--checks">
                    <h2 className="modal__subheading">Display Options</h2>
                    <label>
                        <input type="checkbox" checked={this.state.includeSelfInSeenBluesCount}
                            onChange={this.setIncludeSelfInSeenBluesCount} />
                        Include Self In Seen Blues Count
                    </label>
                    <h2 className="modal__subheading">Puzzle Generation Options</h2>
                    <label>
                        <input type="checkbox" checked={this.state.eliminateRedsFirst}
                            onChange={this.setEliminateRedsFirst} />
                        Eliminate Reds First
                    </label>
                    <label>
                        <input type="checkbox" checked={this.state.sameSolutionAsPreviousGame}
                            onChange={this.setSameSolutionAsPreviousGame} />
                        Same Solution As Previous Game
                    </label>
                </div>

            </Modal>
        </>
        );
    }
}

export default OptionsPopup;
